import {
  Box,
  Grid,
  IconButton,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  ChangeEventType,
  GeolocationLocationSetData,
  IGeolocationSet,
} from '../../../../../types';
import { FloatingButton, PrimaryButton } from '../../../../Buttons';
import FacebookMap from './FacebookMap';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

interface SearchAddressFormProps {
  setFormValues: Dispatch<SetStateAction<IGeolocationSet>>;
  formValues: IGeolocationSet;
  address: string;
  onClose: () => void;
  isEdit?: boolean;
  index?: number | null;
  exclude?: boolean;
}

const SearchAddressForm: React.FC<SearchAddressFormProps> = ({
  setFormValues,
  formValues,
  address,
  onClose,
  index = null,
  isEdit = false,
  exclude = false,
}) => {
  const dispatch = useDispatch();
  const [searchError, setSearchError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [map, setMap] = useState<any>(null);
  const [result, setResult] = useState<GeolocationLocationSetData>({
    address_string: '',
    latitude: null,
    longitude: null,
    radius: 50,
    distance_unit: 'mile',
  });

  useEffect(() => {
    if (address && map) {
      setSearchQuery(address);
      geodecodeAddress(address);
    }
  }, [address, map]);

  const getCoordinates = async (address: string) => {
    const request = {
      query: address,
      fields: ['ALL'],
    };

    let service = new google.maps.places.PlacesService(map);

    return new Promise((resolve, reject) => {
      service.findPlaceFromQuery(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const coordinates = {
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng(),
            placeId: results[0].place_id,
          };
          resolve(coordinates);
        } else if (
          status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          reject(new Error(`Place query failed with status: ${status}`));
        } else {
          reject(new Error(`Place query failed with status: ${status}`));
        }
      });
    });
  };

  const geodecodeAddress = (address: string) => {
    getCoordinates(address)
      .then((coordinates: any) => {
        setResult({
          ...result,
          radius: 50,
          address_string: address,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
        });
        setSearchError(null);
      })
      .catch((error: any) => {
        console.log(error);
        setSearchError(
          'We cannot find your place. Use the map below to search and pin for your location',
        );
        setResult({
          ...result,
          radius: 0,
          address_string: '',
          latitude: null,
          longitude: null,
        });
      });
  };

  const handleEnterAddress = (excluded: boolean) => {
    if (!result.latitude || !result.longitude) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'Please enter a valid address',
          type: 'error',
        }),
      );
    }

    const data = () => {
      if (excluded) {
        return formValues?.excludedGeolocations;
      }

      return formValues?.geolocations;
    };

    const existingCustomLocations = (data()?.custom_locations || [])?.filter(
      (location: GeolocationLocationSetData) => {
        return (
          location.latitude === result.latitude &&
          location.longitude === result.longitude
        );
      },
    );

    if (existingCustomLocations.length > 0) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'This location already exists',
          type: 'error',
        }),
      );
    }

    const temp = [...(data()?.custom_locations || []), result];

    let tempFormValues: IGeolocationSet = { ...formValues };

    if (excluded) {
      tempFormValues = {
        ...tempFormValues,
        excludedGeolocations: {
          ...formValues.excludedGeolocations,
          custom_locations: temp,
        },
      };
    } else {
      tempFormValues = {
        ...tempFormValues,
        geolocations: {
          ...formValues.geolocations,
          custom_locations: temp,
        },
      };
    }

    setFormValues(tempFormValues);
    onClose();
  };

  const handleSearchLocation = () => {
    if (!searchQuery) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: "Please enter the address you'd like to search",
          type: 'error',
        }),
      );
    }

    geodecodeAddress(searchQuery);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ marginBottom: '40px' }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            variant="standard"
            type="text"
            id="name"
            name="address"
            label="Address"
            onChange={(e: ChangeEventType) => setSearchQuery(e.target.value)}
            value={searchQuery}
            InputLabelProps={{ shrink: true }}
            size="small"
            autoFocus
            placeholder="Enter the address to search"
            helperText="Suggested Format: Street Address, City, State, Country"
            onKeyDown={(e: any) => {
              if (e.keyCode === 13 && !e.shiftKey) {
                handleSearchLocation();
                e.preventDefault();
              }
            }}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => handleSearchLocation()}>
                  <SearchOutlinedIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <PrimaryButton
            title="Search Address"
            type="button"
            handleOnClick={handleSearchLocation}
          />
        </Grid>

        {!result.latitude || !result.longitude ? (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ color: 'red', fontWeight: 'bold' }}
            >
              {searchError}
            </Typography>
          </Grid>
        ) : null}

        {result.latitude && result.longitude ? (
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography component="div" variant="caption">{`${
              result?.radius || 50
            } miles`}</Typography>

            <Slider
              component="div"
              defaultValue={50}
              min={1}
              max={50}
              valueLabelDisplay="auto"
              value={result?.radius || 50}
              onChange={(e: any) =>
                setResult({ ...result, radius: parseInt(e.target.value) })
              }
              sx={{ width: '300px' }}
            />
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ color: 'red', fontWeight: 'bold', textAlign: 'center' }}
          >
            If your location is incorrect, please tap the map to place a pin at
            your precise location.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FacebookMap
            geolocations={formValues?.geolocations}
            excludedGeolocations={formValues?.excludedGeolocations}
            setParentMap={setMap}
            search
            searchResult={result}
            setSearchResult={setResult}
          />
        </Grid>
      </Grid>

      {isEdit ? (
        <PrimaryButton title="Update" type="button" />
      ) : (
        <>
          <FloatingButton
            title="Exclude"
            onClick={() => handleEnterAddress(true)}
            bottom="0px"
            right="5px"
            sticky
            disabled={!result.latitude || !result.longitude}
            theme="red"
            stickyPosition="right"
          />

          <FloatingButton
            title="Include"
            onClick={() => handleEnterAddress(false)}
            bottom="0px"
            right="110px"
            sticky
            disabled={!result.latitude || !result.longitude}
            stickyPosition="right"
          />
        </>
      )}
    </Box>
  );
};

export default SearchAddressForm;
